import { useState } from 'react'
import {
  Button,
  DateInput,
  DatePicker,
  DatePickerProps,
  DateSegment,
  Dialog,
  Group,
  Label,
  Popover,
  TimeField,
} from 'react-aria-components'
import {
  ZonedDateTime,
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal,
  toCalendarDate,
  toTime,
  toZoned,
} from '@internationalized/date'

import * as styles from './DateTimePicker.styles'
import { Calendar } from './Calendar'
import { isPast } from '../../../utilities/dateTimeUtils'

interface Props {
  onChange: (dateTime: ZonedDateTime) => void
  initialDateTime?: ZonedDateTime
  noTimePicker?: boolean
  isDateUnavailable?: DatePickerProps<ZonedDateTime>['isDateUnavailable']
}

export function DateTimePicker({
  onChange,
  initialDateTime,
  noTimePicker,
  isDateUnavailable,
}: Props) {
  let initialDate: ZonedDateTime

  let todayDate = now(getLocalTimeZone())

  if (typeof initialDateTime === 'string') {
    initialDate = parseAbsoluteToLocal(initialDateTime)
  } else if (!initialDateTime) {
    initialDate = todayDate
  } else {
    initialDate = initialDateTime
  }

  let localTimeZone = initialDate.timeZone ?? getLocalTimeZone()

  let [date, setDate] = useState(initialDate)
  let zonedDateTime = toZoned(date, localTimeZone)
  let initialTime = toTime(zonedDateTime)
  let [time, setTime] = useState(initialTime)

  return (
    <DatePicker
      css={styles.container}
      value={toCalendarDate(date)}
      onChange={(newDate) => {
        let zoned = toZoned(newDate, localTimeZone).set({
          hour: time.hour,
          minute: time.minute,
        })

        onChange(zoned)
        setDate(zoned)
      }}
      isDateUnavailable={isDateUnavailable || isPast}
      name="date"
    >
      <Label css={styles.hidden}>Date</Label>
      <Group css={styles.datePickerGroup}>
        <DateInput css={styles.dateInput}>
          {(segment) => (
            <DateSegment css={styles.dateSegment} segment={segment} />
          )}
        </DateInput>
        <Button css={styles.popoverOpenButton}>
          <i className="fa fa-chevron-down" />
        </Button>
      </Group>
      <Popover css={styles.popover}>
        <Dialog>
          <Calendar />
        </Dialog>
      </Popover>

      {!noTimePicker && (
        <TimeField
          css={styles.timeContainer}
          value={time}
          granularity="minute"
          onChange={(newTime) => {
            let newDate = toZoned(date, localTimeZone).set({
              hour: newTime.hour,
              minute: newTime.minute,
            })
            onChange(newDate)
            setTime(newTime)
          }}
          name="time"
        >
          <Label css={styles.hidden}>Time</Label>
          <DateInput css={styles.timeInput} aria-label="Time">
            {(segment) => <DateSegment segment={segment} />}
          </DateInput>
        </TimeField>
      )}
    </DatePicker>
  )
}
